import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  Mic,
  ChevronDown,
  Play,
  Square,
  Download,
  Copy,
  Check,
  Loader2,
  Volume2,
  Pause,
  RotateCcw,
  Trash2,
  Lock,
  ArrowRightLeft
} from "lucide-react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TokenBalanceCard from "../TokenBalanceCard";
import PlanStatusCard from "../PlanStatusCard";
import TranslationFeedback from "../TranslationFeedback";
import BetaModelBanner from "./BetaModelBanner";
import { getSubscription } from "../../../services/paymentService";
import { getApiKeys, getApiLogs } from "../../../services/developerService";
import { getUserById } from "../../../services/authService";
import { API_BASE_URL } from "../../../constant/constant";
import { translateSpeech } from "../../../services/speechService";
import { estimateAudioTokens, getAudioFileDuration, TOKEN_RATES } from "../../../utils/tokenCalculator";
import { LANGUAGES } from "../../../constant/constant";

const MAX_SPEECH_AUDIO_SECONDS = 30;
// ─── Constants ────────────────────────────────────────────────────────────────

// Mock waveform bar heights for playback visualization
const WAVEFORM_BARS = [
  18, 32, 24, 40, 28, 52, 36, 20, 44, 30, 56, 38, 22, 48, 34, 26, 50, 32, 42, 24,
  54, 28, 46, 38, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 42, 24, 54, 28, 46,
  36, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 16, 14, 20, 30, 18, 22, 26, 20,
  30, 14, 18, 24, 28, 20, 16, 22, 18, 30, 14, 24, 20, 16, 28, 22, 18, 30, 14, 24,
];

// Helper to format dates to time-ago strings
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = Math.floor(seconds / 31536000);

  if (interval >= 1) return interval === 1 ? '1 year ago' : `${interval} years ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return interval === 1 ? '1 month ago' : `${interval} months ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return interval === 1 ? '1 day ago' : `${interval} days ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return interval === 1 ? '1 hour ago' : `${interval} hours ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return interval === 1 ? '1 minute ago' : `${interval} minutes ago`;
  return seconds < 10 ? 'just now' : `${Math.floor(seconds)} seconds ago`;
};

// Helper to format seconds to time display (M:SS)
const formatTimeDisplay = (timeSecs) => {
  if (!timeSecs || isNaN(timeSecs)) return "0:00";
  const mins = Math.floor(timeSecs / 60);
  const secs = Math.round(timeSecs % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Waveform = ({ isPlaying, progress }) => (
  <div className="flex items-end gap-[2px] h-14 w-full">
    {WAVEFORM_BARS.map((h, i) => {
      const filled = i / WAVEFORM_BARS.length < progress;
      return (
        <div
          key={i}
          className={`flex-1 rounded-sm transition-all ${filled ? "bg-white" : "bg-white/30"
            }`}
          style={{
            height: `${h}px`,
            ...(isPlaying && filled
              ? {
                animationName: "wavePulse",
                animationDuration: `${0.3 + (i % 5) * 0.1}s`,
                animationIterationCount: "infinite",
                animationDirection: "alternate",
                animationTimingFunction: "ease-in-out",
              }
              : {}),
          }}
        />
      );
    })}
    <style>{`
      @keyframes wavePulse {
        from { transform: scaleY(0.6); }
        to   { transform: scaleY(1); }
      }
    `}</style>
  </div>
);

const LanguageDropdown = ({ value, onChange, options = LANGUAGES }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 font-medium bg-white hover:border-gray-300 transition-colors"
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-48 overflow-y-auto">
          {options.map((lang) => (
            <button
              key={lang}
              onClick={() => { onChange(lang); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${value === lang
                ? "bg-[#F0FAF6] text-[#2D7A5F] font-semibold"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UploadZone = ({ onFile, file, onClear }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl bg-[#EEF5F3] border border-[#C8E0D8] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white border border-[#C8E0D8] flex items-center justify-center flex-shrink-0">
            <Upload className="w-4 h-4 text-[#2D7A5F]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {(file.size / (1024 * 1024)).toFixed(2)} MB · {file.type || "audio"}
            </p>
          </div>
        </div>
        <button
          onClick={onClear}
          className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
        >
          <span className="text-sm leading-none">×</span>
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${dragging
        ? "border-2 border-dashed border-[#2D7A5F] bg-[#F0FAF6]"
        : "border-2 border-dashed border-gray-200 bg-[#EEF5F3] hover:border-[#B2D8CC] hover:bg-[#E6F2EF]"
        }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const selected = e.target.files?.[0];
          e.target.value = "";
          if (selected) onFile(selected);
        }}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-[#2D7A5F]/10 flex items-center justify-center mb-1">
          <Upload className="w-5 h-5 text-[#2D7A5F]" />
        </div>
        <p className="text-sm font-semibold text-gray-800">Drop audio file or click to upload</p>
        <p className="text-xs text-gray-400">MP3 or WAV · up to 30 seconds · 25 MB</p>
      </div>
    </div>
  );
};

const HistoryItem = ({ item }) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-900">{item.title}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-gray-500">{item.lang}</span>
        <span className="text-gray-300 text-xs">•</span>
        <span className="text-xs text-gray-500">
          Tokens used:{" "}
          <span className="font-semibold text-gray-700">{item.tokens}</span>
        </span>
        {item.mode && (
          <>
            <span className="text-gray-300 text-xs">•</span>
            <span className="text-xs text-gray-500">{item.mode}</span>
          </>
        )}
      </div>
    </div>
    <span className="text-xs text-gray-400 flex-shrink-0 ml-4">{item.time}</span>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SpeechTranslations = () => {
  const { token } = useSelector((state) => state.auth);

  // Subscription & User Details State
  const [checkingData, setCheckingData] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [apiKey, setApiKey] = useState(null);

  // Speech Translation Config & State
  const [activeTab, setActiveTab] = useState("upload"); // "upload" | "record"
  const [sourceLang, setSourceLang] = useState("Swahili");
  const [targetLang, setTargetLang] = useState("English");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translation, setTranslation] = useState("");
  const [translatedAudioUrl, setTranslatedAudioUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedApi, setCopiedApi] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);

  const [stats, setStats] = useState({
    words: 0,
    tokensConsumed: 0,
    duration: "0 : 00",
    processingTime: "0.00s",
    durationSecs: 0,
  });

  const [history, setHistory] = useState([]);

  // Web Audio Recording States
  const [recordingState, setRecordingState] = useState("idle");
  const [recordDuration, setRecordDuration] = useState(0);
  const timerRef = useRef(null);
  const recordSecondsRef = useRef(0);

  // MediaRecorder references
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [audioUrl, setAudioUrl] = useState(null);

  // Playback preview reference
  const previewAudioRef = useRef(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);

  // Translated Audio Output Reference & State
  const translatedAudioRef = useRef(null);
  const [isPlayingTranslated, setIsPlayingTranslated] = useState(false);
  const [translatedCurrentTime, setTranslatedCurrentTime] = useState(0);
  const [translatedDuration, setTranslatedDuration] = useState(0);
  const [translatedVolume, setTranslatedVolume] = useState(0.8);

  // Retrieve details and logs
  const fetchDashboardData = async () => {
    if (!token) return;
    try {
      // 1. User details
      const userRes = await getUserById(token);
      if (userRes) {
        setUserDetails(userRes);

        if (userRes.tokenExpiration) {
          const now = new Date();
          const exp = new Date(userRes.tokenExpiration);
          const diffTime = exp.getTime() - now.getTime();
          const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
          setRemainingDays(diffDays);
        }
      }

      // 2. Subscription status
      const subResponse = await getSubscription(token);
      if (subResponse.success && subResponse.subscription) {
        setSubscriptionDetails(subResponse.subscription);
      }

      // 3. API Keys
      const keysResponse = await getApiKeys(token);
      const activeKey = keysResponse.apiKeys?.find(key => key.isActive);
      if (activeKey) {
        setApiKey(activeKey.key);
      }

      // 4. API Logs for history
      const logsResponse = await getApiLogs({ limit: 20 }, token);
      if (logsResponse?.logs) {
        const stLogs = logsResponse.logs.filter(
          log => (log.service && (log.service.toUpperCase() === 'STS' || log.service.toUpperCase() === 'ST')) || (log.endpoint && log.endpoint.includes('/st/'))
        );
        const mappedHistory = stLogs.map((log) => {
          const actionParts = (log.action || '').split(' • ');

          const time = log.when || (log.createdAt ? formatTimeAgo(new Date(log.createdAt)) : '');

          const sourceLang = log.sourceLang || log.successDetails?.requestData?.source_language || 'Swahili';
          const targetLang = log.targetLang || log.successDetails?.requestData?.target_language || 'English';

          const title = actionParts[1] ? `Translated speech (${actionParts[1]})` : 'Translated speech';
          const tokens = log.token ?? log.tokensUsed ?? 0;
          const mode = log.mode || 'Audio Translation';

          return {
            title,
            lang: `${sourceLang} to ${targetLang}`,
            tokens,
            mode,
            time
          };
        });
        setHistory(mappedHistory);
      }

    } catch (error) {
      console.error("Error loading dashboard data for Speech Translation:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setCheckingData(true);
      await fetchDashboardData();
      setCheckingData(false);
    };
    init();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getAvailableTargetLanguages = () => {
    return ["English", "Swahili"].filter((lang) => lang !== sourceLang);
  };

  const handleSourceLanguageChange = (lang) => {
    setSourceLang(lang);
    if (lang === "Swahili") setTargetLang("English");
    else if (lang === "English") setTargetLang("Swahili");
  };

  const handleTargetLanguageChange = (lang) => {
    setTargetLang(lang);
    if (lang === "Swahili") setSourceLang("English");
    else if (lang === "English") setSourceLang("Swahili");
  };

  const handleSwapLanguages = () => {
    const prevSource = sourceLang;
    setSourceLang(targetLang);
    setTargetLang(prevSource);
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const checkTokenStatus = () => {
    if (!userDetails) {
      return { expired: false, exhausted: true };
    }
    const tokensAvailable = userDetails.tokens !== undefined && userDetails.tokens !== null && userDetails.tokens > 0;
    const exhausted = !tokensAvailable;
    let expired = false;
    if (userDetails.tokenExpiration) {
      const expirationDate = new Date(userDetails.tokenExpiration);
      const currentDate = new Date();
      expired = currentDate > expirationDate;
    }
    return { expired, exhausted };
  };

  // Perform Speech Translation API Call
  const handleTranslate = async () => {
    if (!uploadedFile || loading) return;

    const { expired, exhausted } = checkTokenStatus();
    if (expired) {
      toast.error("Your tokens have expired. Please purchase a plan or renew your subscription.");
      return;
    }
    if (exhausted) {
      toast.error("Insufficient tokens. Please purchase a top-up plan.");
      return;
    }

    let inputDur = activeTab === "upload" ? audioDuration : recordDuration;
    if ((!inputDur || !Number.isFinite(inputDur)) && uploadedFile) {
      inputDur = await getAudioFileDuration(uploadedFile);
    }
    const safeInputDur = Number.isFinite(inputDur) && inputDur > 0 ? inputDur : 0;
    if (safeInputDur <= 0) {
      toast.error("Couldn't read this audio length. Use an MP3 or WAV that is 30 seconds or shorter.");
      return;
    }
    if (safeInputDur > MAX_SPEECH_AUDIO_SECONDS) {
      toast.error(`Audio must be ${MAX_SPEECH_AUDIO_SECONDS} seconds or shorter.`);
      return;
    }

    setLoading(true);
    setTranslation("");
    if (translatedAudioRef.current) {
      translatedAudioRef.current.pause();
      translatedAudioRef.current.currentTime = 0;
    }
    setIsPlayingTranslated(false);
    setTranslatedCurrentTime(0);
    setTranslatedDuration(0);
    if (translatedAudioUrl) {
      URL.revokeObjectURL(translatedAudioUrl);
      setTranslatedAudioUrl(null);
    }
    const startTime = Date.now();

    try {
      const pair = `${sourceLang}-to-${targetLang}`;
      const result = await translateSpeech(uploadedFile, pair, token, safeInputDur);

      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        setTranslatedAudioUrl(url);
        setShowFeedback(true);
        toast.success("Speech translated successfully!");

        const tokens = estimateAudioTokens(safeInputDur, TOKEN_RATES.ST_TOKENS_PER_SECOND);

        // 2. Measure translated output audio duration to match player
        let outputDur = await getAudioFileDuration(result);
        if (!Number.isFinite(outputDur) || outputDur <= 0) {
          outputDur = safeInputDur;
        }
        const safeOutputDur = Number.isFinite(outputDur) && outputDur > 0 ? outputDur : 0;
        setTranslatedDuration(safeOutputDur);

        const procSecs = ((Date.now() - startTime) / 1000).toFixed(2);
        const formattedDur = formatTimeDisplay(safeOutputDur);

        setStats({
          words: 0,
          tokensConsumed: tokens,
          duration: formattedDur,
          processingTime: `${procSecs}s`,
          durationSecs: safeOutputDur,
        });

        await fetchDashboardData();
      } else if (result.status === "success" || result.translation) {
        const transText = result.translation || "";
        setTranslation(transText);
        setShowFeedback(true);
        toast.success("Speech translated successfully!");

        // Use determined audio duration
        const safeDur = safeInputDur;
        const procSecs = ((Date.now() - startTime) / 1000).toFixed(2);
        const wordCount = transText.trim().split(/\s+/).filter(Boolean).length;
        const tokens = Number.isFinite(result.tokensUsed) ? result.tokensUsed : estimateAudioTokens(safeDur, TOKEN_RATES.ST_TOKENS_PER_SECOND);

        const formattedDur = formatTimeDisplay(safeDur);

        setStats({
          words: wordCount,
          tokensConsumed: tokens,
          duration: formattedDur,
          processingTime: `${procSecs}s`,
          durationSecs: safeDur,
        });

        // Refresh token balances & history logs
        await fetchDashboardData();
      } else {
        throw new Error(result.message || "Failed to translate speech.");
      }
    } catch (error) {
      console.error("Speech translation error:", error);
      toast.error(error.message || "Failed to translate audio.");
    } finally {
      setLoading(false);
    }
  };

  // Web Audio Recording functionality
  const stopRecordingAtLimit = () => {
    stopRecording();
    toast.error(`Audio is limited to ${MAX_SPEECH_AUDIO_SECONDS} seconds.`);
  };

  const beginDurationTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const next = recordSecondsRef.current + 1;
      if (next >= MAX_SPEECH_AUDIO_SECONDS) {
        recordSecondsRef.current = MAX_SPEECH_AUDIO_SECONDS;
        setRecordDuration(MAX_SPEECH_AUDIO_SECONDS);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        stopRecordingAtLimit();
        return;
      }
      recordSecondsRef.current = next;
      setRecordDuration(next);
    }, 1000);
  };

  const startRecording = async () => {
    audioChunksRef.current = [];
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setTranslation("");
    setUploadedFile(null);
    setAudioUrl(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        const recordFile = new File([audioBlob], `recording-${Date.now()}.mp3`, {
          type: "audio/mp3",
          lastModified: Date.now(),
        });
        setUploadedFile(recordFile);
      };

      mediaRecorder.start();
      setRecordingState("recording");
      beginDurationTimer();
    } catch (err) {
      console.error("Microphone access denied or unsupported", err);
      toast.error("Could not access microphone. Please confirm browser permissions.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecordingState("paused");
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
    }
    setRecordingState("recording");
    beginDurationTimer();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecordingState("finished");
  };

  const deleteRecording = () => {
    setRecordingState("idle");
    setUploadedFile(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setIsPlayingPreview(false);
    setPreviewProgress(0);
  };

  const handleClear = () => {
    deleteRecording();
    setTranslation("");
    setShowFeedback(false);
    if (translatedAudioRef.current) {
      translatedAudioRef.current.pause();
      translatedAudioRef.current.currentTime = 0;
    }
    setIsPlayingTranslated(false);
    setTranslatedCurrentTime(0);
    setTranslatedDuration(0);
    if (translatedAudioUrl) {
      URL.revokeObjectURL(translatedAudioUrl);
      setTranslatedAudioUrl(null);
    }
    setAudioDuration(0);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
  };

  // Preview Playback Controls
  const handleTogglePreviewPlay = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      previewAudioRef.current.play().then(() => {
        setIsPlayingPreview(true);
      }).catch(err => {
        console.error("Preview playback failed:", err);
      });
    }
  };

  // Translated Audio Playback Event Handlers
  const handlePlayPauseTranslated = () => {
    if (!translatedAudioUrl || !translatedAudioRef.current) return;
    if (isPlayingTranslated) {
      translatedAudioRef.current.pause();
      setIsPlayingTranslated(false);
    } else {
      translatedAudioRef.current.play().then(() => {
        setIsPlayingTranslated(true);
      }).catch(err => {
        console.error("Translated audio playback failed:", err);
      });
    }
  };

  const handleStopTranslated = () => {
    if (!translatedAudioRef.current) return;
    translatedAudioRef.current.pause();
    translatedAudioRef.current.currentTime = 0;
    setIsPlayingTranslated(false);
    setTranslatedCurrentTime(0);
  };

  const handleTranslatedTimeUpdate = () => {
    if (!translatedAudioRef.current) return;
    setTranslatedCurrentTime(translatedAudioRef.current.currentTime);
  };

  const handleTranslatedLoadedMetadata = () => {
    if (!translatedAudioRef.current) return;
    const dur = translatedAudioRef.current.duration;
    setTranslatedDuration(Number.isFinite(dur) && dur > 0 ? dur : 0);
  };

  const handleTranslatedEnded = () => {
    setIsPlayingTranslated(false);
    setTranslatedCurrentTime(0);
  };

  useEffect(() => {
    if (translatedAudioRef.current) {
      translatedAudioRef.current.volume = translatedVolume;
    }
  }, [translatedVolume]);

  const handlePreviewAudioTimeUpdate = () => {
    if (!previewAudioRef.current) return;
    const dur = previewAudioRef.current.duration;
    const progress = (Number.isFinite(dur) && dur > 0) ? (previewAudioRef.current.currentTime / dur) : 0;
    setPreviewProgress(isNaN(progress) || !Number.isFinite(progress) ? 0 : progress);
  };

  const handlePreviewAudioEnded = () => {
    setIsPlayingPreview(false);
    setPreviewProgress(0);
  };

  const handleFile = async (file) => {
    if (!file) {
      setUploadedFile(null);
      setAudioDuration(0);
      return;
    }

    const dur = await getAudioFileDuration(file);
    if (!Number.isFinite(dur) || dur <= 0) {
      setUploadedFile(null);
      setAudioDuration(0);
      toast.error("Couldn't read this audio length. Use an MP3 or WAV that is 30 seconds or shorter.");
      return;
    }
    if (dur > MAX_SPEECH_AUDIO_SECONDS) {
      setUploadedFile(null);
      setAudioDuration(0);
      toast.error(`Audio must be ${MAX_SPEECH_AUDIO_SECONDS} seconds or shorter.`);
      return;
    }

    setUploadedFile(file);
    setTranslation("");
    setAudioDuration(dur);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(translation);
    setCopied(true);
    toast.success("Translation copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportText = () => {
    if (!translation) return;
    const element = document.createElement("a");
    const file = new Blob([translation], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("Translation exported successfully!");
  };

  const handleExportJSON = () => {
    if (!translation) return;
    const jsonObject = {
      sourceLanguage: sourceLang,
      targetLanguage: targetLang,
      words: stats.words,
      tokensConsumed: stats.tokensConsumed,
      duration: stats.duration,
      processingTime: stats.processingTime,
      translation,
    };
    const blob = new Blob([JSON.stringify(jsonObject, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = url;
    element.download = `speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}-${Date.now()}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
    toast.success("JSON exported successfully!");
  };

  const handleExportSRT = () => {
    if (!translation) return;

    const formatSrtTime = (seconds) => {
      const hrs = Math.floor(seconds / 3600).toString().padStart(2, "0");
      const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
      const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
      const ms = Math.floor((seconds % 1) * 1000).toString().padStart(3, "0");
      return `${hrs}:${mins}:${secs},${ms}`;
    };

    const sentences = translation.match(/[^.!?]+[.!?]+/g) || [translation];
    const totalDuration = stats.durationSecs || Math.max(sentences.length * 3, 5);
    const sentenceDuration = totalDuration / sentences.length;

    let srtContent = "";
    sentences.forEach((sentence, index) => {
      const startTime = index * sentenceDuration;
      const endTime = (index + 1) * sentenceDuration;
      srtContent += `${index + 1}\n${formatSrtTime(startTime)} --> ${formatSrtTime(endTime)}\n${sentence.trim()}\n\n`;
    });

    const blob = new Blob([srtContent.trim()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = url;
    element.download = `speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}-${Date.now()}.srt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
    toast.success("SRT subtitle file exported!");
  };

  const handleCopyApi = () => {
    const pairStr = `${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}`.replace(/\s+/g, '-');
    const curl = `curl -X 'POST' \\
  '${API_BASE_URL}/st/v1/${pairStr}/with-api-key' \\
  -H 'accept: application/json' \\
  -H 'Authorization: Bearer ${apiKey || 'YOUR_API_KEY_HERE'}' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'audio_file=@${uploadedFile ? uploadedFile.name : 'recording.mp3'};type=audio/mpeg'`;
    navigator.clipboard.writeText(curl);
    setCopiedApi(true);
    toast.success("Curl command request copied!");
    setTimeout(() => setCopiedApi(false), 2000);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setUploadedFile(null);
    setTranslation("");
    if (translatedAudioRef.current) {
      translatedAudioRef.current.pause();
      translatedAudioRef.current.currentTime = 0;
    }
    setIsPlayingTranslated(false);
    setTranslatedCurrentTime(0);
    setTranslatedDuration(0);
    setRecordingState("idle");
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setIsPlayingPreview(false);
    setPreviewProgress(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  if (checkingData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { expired: tokensExpired, exhausted: tokensExhausted } = checkTokenStatus();
  const lockUi = tokensExpired || tokensExhausted;

  if (lockUi) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[600px]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-xl w-full text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Premium feature locked
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            This Speech Translation tool is premium and requires an active subscription or valid token balance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto space-y-5">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Audio element for preview playback ── */}
      {audioUrl && (
        <audio
          ref={previewAudioRef}
          src={audioUrl}
          onTimeUpdate={handlePreviewAudioTimeUpdate}
          onEnded={handlePreviewAudioEnded}
          className="hidden"
        />
      )}

      {/* ── Audio element for translated audio output ── */}
      {translatedAudioUrl && (
        <audio
          ref={translatedAudioRef}
          src={translatedAudioUrl}
          onTimeUpdate={handleTranslatedTimeUpdate}
          onLoadedMetadata={handleTranslatedLoadedMetadata}
          onEnded={handleTranslatedEnded}
          className="hidden"
        />
      )}

      {/* ── Beta Models Banner ── */}
      <BetaModelBanner />

      {/* ── Row 1: Token Balance + Plan Status ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TokenBalanceCard
          userDetails={userDetails}
          remainingDays={remainingDays}
          subscriptionDetails={subscriptionDetails}
        />
        <PlanStatusCard
          subscriptionDetails={subscriptionDetails}
        />
      </div>

      {/* ── Row 2: Input Tab Panel (50%) + Controls Panel (50%) ── */}
      <div className="flex gap-4 flex-col lg:flex-row items-start">

        {/* Left: File Upload / Record */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5">
          {/* Tab Switcher */}
          <div className="flex gap-1 mb-5">
            <button
              onClick={() => switchTab("upload")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${activeTab === "upload"
                ? "bg-white border-gray-200 text-gray-900 shadow-sm"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={() => switchTab("record")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${activeTab === "record"
                ? "bg-white border-gray-200 text-gray-900 shadow-sm"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <Mic className="w-4 h-4" />
              Record
            </button>
          </div>

          {/* Upload Zone */}
          {activeTab === "upload" && (
            <UploadZone
              onFile={handleFile}
              file={uploadedFile}
              onClear={() => { setUploadedFile(null); setTranslation(""); setAudioDuration(0); }}
            />
          )}

          {/* Advanced Record Zone */}
          {activeTab === "record" && (
            <div className="rounded-2xl bg-[#EEF5F3] border border-[#C8E0D8] p-6 flex flex-col gap-6">

              {/* State 1: IDLE */}
              {recordingState === "idle" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-600 tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      <span>READY TO RECORD</span>
                    </div>
                    <span>{formatDuration(0)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-gray-400 font-mono">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={startRecording}
                      className="flex items-center gap-2 bg-[#D13B3B] hover:bg-[#B22E2E] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Mic className="w-4 h-4" />
                      Start recording
                    </button>
                  </div>
                </div>
              )}

              {/* State 2: RECORDING */}
              {recordingState === "recording" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-[#D13B3B] tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#D13B3B] animate-pulse" />
                      <span>RECORDING</span>
                    </div>
                    <span className="font-mono text-gray-700">{formatDuration(recordDuration)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-[#D13B3B] font-mono animate-pulse">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center gap-3">
                    <button
                      onClick={pauseRecording}
                      className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-sm"
                    >
                      <Pause className="w-4 h-4 text-gray-700 fill-gray-700" />
                      Pause
                    </button>
                    <button
                      onClick={stopRecording}
                      className="flex items-center gap-2 bg-[#0C101A] hover:bg-[#1C202A] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Square className="w-4 h-4 text-white fill-white" />
                      Stop
                    </button>
                  </div>
                </div>
              )}

              {/* State 3: PAUSED */}
              {recordingState === "paused" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-[#ef8702] tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#ef8702]" />
                      <span>PAUSED</span>
                    </div>
                    <span className="font-mono text-gray-700">{formatDuration(recordDuration)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-gray-400 font-mono">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center gap-3">
                    <button
                      onClick={resumeRecording}
                      className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Play className="w-4 h-4 text-white fill-white" />
                      Resume
                    </button>
                    <button
                      onClick={stopRecording}
                      className="flex items-center gap-2 bg-[#0C101A] hover:bg-[#1C202A] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Square className="w-4 h-4 text-white fill-white" />
                      Stop
                    </button>
                  </div>
                </div>
              )}

              {/* State 4: FINISHED */}
              {recordingState === "finished" && uploadedFile && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between bg-white border border-[#C8E0D8] p-4 rounded-xl flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#EEF5F3] flex items-center justify-center text-[#2D7A5F]">
                        <Mic className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{uploadedFile.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Recording · {(uploadedFile.size / 1024).toFixed(1)} KB · {formatDuration(recordDuration)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={startRecording}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Re-record
                      </button>
                      <button
                        onClick={deleteRecording}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 text-xs font-semibold text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Built-in Recording Preview Player */}
                  <div className="flex items-center gap-3 px-1">
                    <button
                      onClick={handleTogglePreviewPlay}
                      className="w-9 h-9 rounded-full bg-[#1e6c56] text-white flex items-center justify-center hover:bg-[#175443] transition-all flex-shrink-0"
                    >
                      {isPlayingPreview ? (
                        <Pause className="w-4 h-4 fill-white text-white" />
                      ) : (
                        <Play className="w-4 h-4 fill-white text-white ml-0.5" />
                      )}
                    </button>
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-[#1e6c56]"
                        style={{ width: `${previewProgress * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 font-mono">{formatDuration(recordDuration)}</span>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Right: Source Language + Target Language + Translate */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
          {/* Language Selection */}
          <div className="space-y-4">
            {/* Source Language */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                Source Language
              </p>
              <LanguageDropdown
                value={sourceLang}
                onChange={handleSourceLanguageChange}
                options={["Swahili", "English"]}
              />
            </div>

            {/* Swap Languages Button */}
            <div className="flex items-center justify-center -my-1">
              <button
                type="button"
                onClick={handleSwapLanguages}
                className="p-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors shadow-sm"
                title="Swap source and target languages"
              >
                <ArrowRightLeft className="w-4 h-4 text-[#2D7A5F]" />
              </button>
            </div>

            {/* Target Language */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                Target Language
              </p>
              <LanguageDropdown
                value={targetLang}
                onChange={handleTargetLanguageChange}
                options={getAvailableTargetLanguages()}
              />
            </div>
          </div>

          {/* Translate Actions */}
          <div className="flex justify-end gap-3 flex-wrap">
            {uploadedFile && (
              <button
                onClick={handleCopyApi}
                className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                {copiedApi ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                Copy API request
              </button>
            )}
            {uploadedFile && estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.ST_TOKENS_PER_SECOND) > 0 && (
              <span className="text-xs text-gray-500 self-center">
                Estimated: {estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.ST_TOKENS_PER_SECOND)} {estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.ST_TOKENS_PER_SECOND) === 1 ? 'Token' : 'Tokens'}
              </span>
            )}
            <button
              type="button"
              onClick={handleClear}
              disabled={!uploadedFile && !translation}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors bg-white"
              title="Clear audio file and translation"
            >
              <RotateCcw className="w-4 h-4 text-gray-500" />
              Clear
            </button>
            <button
              onClick={handleTranslate}
              disabled={loading || !uploadedFile}
              className="flex items-center gap-2 bg-[#2B6651] hover:bg-[#235443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Translating…" : "Translate Speech"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Row 3: Output Translation Card ── */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin text-[#2D7A5F]" />
            <p className="text-sm font-medium">Translating speech…</p>
          </div>
        </div>
      ) : translatedAudioUrl ? (
        <div className="bg-[#2B6651] rounded-2xl p-5 shadow-sm">
          {/* Label + filename */}
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <p className="text-xs font-semibold text-[#7EC8A8] uppercase tracking-widest mb-1">
                Translated Speech (Audio Output)
              </p>
              <p className="text-white text-sm font-medium">
                {`speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}.mp3`}
              </p>
            </div>
            <a
              href={translatedAudioUrl}
              download={`speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}-${Date.now()}.mp3`}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#7EC8A8] hover:text-white transition-colors"
            >
              <Download className="w-4 h-4" /> Download MP3
            </a>
          </div>

          {/* Waveform */}
          <Waveform isPlaying={isPlayingTranslated} progress={translatedDuration ? (translatedCurrentTime / translatedDuration) : 0} />

          {/* Controls row */}
          <div className="flex items-center gap-3 mt-4">
            {/* Play/Pause */}
            <button
              onClick={handlePlayPauseTranslated}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              {isPlayingTranslated
                ? <div className="flex gap-[3px]"><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /></div>
                : <Play className="w-4 h-4 text-[#2B6651] ml-0.5" />
              }
            </button>

            {/* Stop */}
            <button
              onClick={handleStopTranslated}
              className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
            >
              <Square className="w-3.5 h-3.5 text-white fill-white" />
            </button>

            {/* Progress track */}
            <div className="flex-1 flex items-center gap-3">
              <div
                className="flex-1 h-[3px] bg-white/30 rounded-full cursor-pointer relative"
                onClick={(e) => {
                  if (!translatedAudioRef.current || !translatedDuration) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const percent = clickX / rect.width;
                  translatedAudioRef.current.currentTime = percent * translatedDuration;
                  setTranslatedCurrentTime(percent * translatedDuration);
                }}
              >
                <div
                  className="h-full bg-white rounded-full transition-all duration-100"
                  style={{ width: `${(translatedDuration ? (translatedCurrentTime / translatedDuration) : 0) * 100}%` }}
                />
              </div>
            </div>

            {/* Timestamp */}
            <span className="text-white/70 text-xs font-mono whitespace-nowrap">
              {formatTimeDisplay(translatedCurrentTime)} / {formatTimeDisplay(translatedDuration)}
            </span>

            {/* Divider */}
            <div className="w-px h-4 bg-white/30" />

            {/* Volume */}
            <Volume2 className="w-4 h-4 text-white/70 flex-shrink-0" />
            <div
              className="w-16 h-[3px] bg-white/30 rounded-full cursor-pointer relative"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const nextVolume = Math.max(0, Math.min(1, clickX / rect.width));
                setTranslatedVolume(nextVolume);
              }}
            >
              <div className="h-full bg-white rounded-full" style={{ width: `${translatedVolume * 100}%` }} />
            </div>
          </div>
        </div>
      ) : translation ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <span className="text-sm font-semibold text-gray-700">Translation</span>
          </div>
          <div className="bg-[#F8FAF9] border border-gray-100 rounded-xl p-4 text-sm text-gray-800 leading-relaxed max-h-60 overflow-y-auto">
            {translation}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="py-8 text-center text-gray-400 text-sm">
            Upload or record an audio file and click "Translate Speech" to view translation here.
          </div>
        </div>
      )}

      {/* ── Stats & Actions Card ── */}
      {(translation || translatedAudioUrl) && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          {/* Top 4 Stats Columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6 border-b border-gray-100 pb-6">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">WORDS</p>
              <p className="text-2xl font-black text-gray-900">{stats.words}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">TOKENS CONSUMED</p>
              <p className="text-2xl font-black text-gray-900">{stats.tokensConsumed}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">DURATION</p>
              <p className="text-2xl font-black text-gray-900">{stats.duration}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">PROCESSING</p>
              <p className="text-2xl font-black text-gray-900">{stats.processingTime}</p>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center gap-3 flex-wrap">
            {translatedAudioUrl ? (
              <a
                href={translatedAudioUrl}
                download={`speech-translation-${sourceLang.toLowerCase()}-to-${targetLang.toLowerCase()}-${Date.now()}.mp3`}
                className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm bg-white"
              >
                <Download className="w-4 h-4 text-gray-700" />
                Download Translated Audio (MP3)
              </a>
            ) : (
              <>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4 text-gray-700" />
                  {copied ? "Copied!" : "Copy Text"}
                </button>
                <button
                  onClick={handleExportText}
                  className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4 text-gray-700" />
                  Download TXT
                </button>
                <button
                  onClick={handleExportJSON}
                  className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4 text-gray-700" />
                  Download JSON
                </button>
                <button
                  onClick={handleExportSRT}
                  className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4 text-gray-700" />
                  Download SRT
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Translation Feedback Card ── */}
      {(translation || translatedAudioUrl) && !loading && showFeedback && (
        <TranslationFeedback
          input={uploadedFile ? uploadedFile.name : "Speech recording"}
          output={translation || "Translated speech audio output"}
          fromLanguage={sourceLang}
          toLanguage={targetLang}
          apiEndpoint="/st/v1/translate"
          onHide={() => setShowFeedback(false)}
        />
      )}

      {/* ── Row 4: Translation History ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Translation History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No speech translation history yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((item, i) => (
              <HistoryItem key={i} item={item} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default SpeechTranslations;
